import { useEffect, useState } from 'react';
import { X, AlertCircle, Mail, CalendarDays } from 'lucide-react';
import { getDiagnosticLeadDetail } from '../api/diagnosticsApi';
import { DiagnosticLeadCaptureDetail } from '../types/diagnostics';
import { mapQuestionsAndAnswers } from '../utils/diagnostics';

interface Props {
  isOpen: boolean;
  leadId: string | null;
  onClose: () => void;
}

export default function DiagnosticLeadModal({ isOpen, leadId, onClose }: Props) {
  const [lead, setLead] = useState<DiagnosticLeadCaptureDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !leadId) {
      return;
    }

    const loadLead = async () => {
      setLoading(true);
      setError(null);
      try {
        const detail = await getDiagnosticLeadDetail(leadId);
        setLead(detail);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load lead details.');
      } finally {
        setLoading(false);
      }
    };

    loadLead();
  }, [isOpen, leadId]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
        <div className="flex items-center justify-between border-b border-strivre-lightnavy p-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Lead Details</h2>
            <p className="text-sm text-slate-500">Review the submitted answers and submission context.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 hover:bg-strivre-softgrey" aria-label="Close lead details">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {loading && <p className="text-sm text-slate-500">Loading lead information...</p>}
          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              <div className="flex items-center gap-2">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            </div>
          )}

          {lead && (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-strivre-lightnavy bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Contact</p>
                  <p className="mt-2 font-semibold text-slate-900">{lead.name || 'Anonymous'}</p>
                  <p className="text-sm text-slate-600">{lead.email}</p>
                  <p className="text-sm text-slate-600">{lead.company}</p>
                </div>
                <div className="rounded-2xl border border-strivre-lightnavy bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Submission</p>
                  <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                    <CalendarDays size={16} />
                    {new Date(lead.createdDateUtc).toLocaleString()}
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                    <Mail size={16} />
                    {lead.isEmailSent ? 'Email sent' : 'Email pending'}
                  </div>
                  {lead.createdByIp && <p className="mt-2 text-sm text-slate-600">IP: {lead.createdByIp}</p>}
                </div>
              </div>

              <div className="rounded-2xl border border-strivre-lightnavy p-4">
                <h3 className="text-lg font-semibold text-slate-900">Question & Answer Review</h3>
                <div className="mt-4 space-y-3">
                  {mapQuestionsAndAnswers(lead.questions, lead.answers).map((item, index) => (
                    <div key={`${item.question}-${index}`} className="rounded-2xl border border-strivre-lightnavy bg-white p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-strivre-softgrey px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                          {item.category}
                        </span>
                        <span className="text-sm font-semibold text-slate-700">{item.questionText}</span>
                      </div>
                      <p className="mt-3 text-sm text-slate-600">{item.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
